/**
 * HUD: broadcast score bug + radar. (§4.7, DECISIONS.md D0)
 *
 * ── Plain DOM, and why that is not laziness ────────────────────────────────
 *
 * §1.1 puts React in the stack for "menus/HUD only — never in the game loop",
 * and §1.1's real requirement is the second clause. A React HUD that re-renders
 * on every score change is fine; one that re-renders at 60Hz allocates VDOM
 * nodes 60 times a second and produces exactly the GC stutter §15.7 bans. The
 * score bug is a dozen text nodes and the radar is a canvas — React would add a
 * reconciler between us and a handful of `textContent` writes, for nothing. If
 * a real menu system lands later it should be React; this should stay as it is.
 *
 * ── Out of the game loop, literally ────────────────────────────────────────
 *
 * `mountHud` starts its own timers. main.ts never calls into the HUD from
 * rAF — it writes plain numbers into a shared `HudState` object, and the HUD
 * reads them on its own clock: the score bug at 5Hz (a match clock ticking at
 * 60Hz is unreadable anyway) and the radar at 20Hz (it is a strategic overview,
 * not an aim reticle — matching SNAPSHOT_SEND_RATE is exactly enough).
 *
 * ── The broadcast score bug (D0) ───────────────────────────────────────────
 *
 * EA-FC-style top-left package: clock segment (+ added-time badge), then team
 * chips / 3-letter codes / score, with a phase strip underneath (KICK OFF,
 * HALF TIME, …). Pure CSS — skewed segments with counter-skewed text give the
 * angular broadcast look without a single image. Team chip colours are NOT
 * hardcoded: they come from `deriveKit` in kit-colors.ts (converted back from
 * linear to sRGB), so the bug and the 3D kits can never drift apart. The radar
 * dots use the same two colours for the same reason.
 *
 * DOM discipline: text nodes are written only when the value actually changed
 * (see `setText`/`setShown`) — the HUD refreshes on a timer but never churns
 * layout on identical frames.
 *
 * ── The radar is not optional ──────────────────────────────────────────────
 *
 * §4.7: "always-on, showing all 22 players, ball, and your assigned zone.
 * Non-negotiable for 11v11." At 11v11 the camera can show maybe a third of the
 * pitch; without the radar, 21 of the 22 players are invisible and off-ball play
 * is guesswork.
 *
 * ── Pitch markings + stable orientation (LIVE INTEGRATION BUGFIX §6) ───────
 *
 * Beyond the halfway line + centre circle, `drawRadar` now also draws both
 * goals (mouth + a shallow depth outline), the penalty boxes (skipped on
 * squad presets narrow enough that the fixed Law-of-the-Game box would just
 * touch both touchlines — see the `boxFits` comment), a distinctly bigger +
 * glowing controlled-player dot with a facing/aim arrow (players.yaw — the
 * sim's real per-player yaw, never camera yaw or ball velocity), and a
 * highlight on the pass receiver `choosePassTarget` would actually strike to
 * while a pass charges.
 *
 * Mirroring: `world.teamAAttacksPositiveX` flips at half time, so mapping
 * world x/z straight to screen would visibly swap which side the local
 * player's goal sits on the moment the half turns over. `drawRadar` instead
 * rotates the WHOLE map 180° (negates both x AND z together — never one axis
 * alone, which would mirror-flip handedness and put a winger's real flank on
 * the wrong side of the map) whenever the local team is not currently
 * attacking +X, so the local player always attacks screen-right in every
 * half, on either team. Every shape — goals, boxes, dots, the arrow — is
 * computed in world space and pushed through the same `sx()`/`sz()`, so
 * nothing needs its own "is this mirrored?" branch.
 */

import {
  CENTRE_CIRCLE_RADIUS,
  FIXED_DT,
  GOAL_DEPTH,
  GOAL_WIDTH,
  MAX_PLAYERS,
  MatchPhase,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
} from '@fg/shared/sim';
import { deriveKit } from '../render/kit-colors.js';
import {
  CROSSHAIR_SIZE_PRESETS,
  DEFAULT_CROSSHAIR_SIZE_INDEX,
  clampCrosshairSizeIndex,
  crosshairDotBox,
  crosshairTicks,
  type CrosshairBox,
} from './crosshair-geometry.js';
import { computeMirrorSign, projectArrowTip, projectX, projectZ } from './minimap-geometry.js';

/**
 * Everything the HUD draws. main.ts owns this object and mutates it in place;
 * the HUD only reads. One allocation for the whole match (§15.7).
 */
export interface HudState {
  scoreA: number;
  scoreB: number;
  /** Elapsed ticks in the current half. */
  clockTicks: number;
  addedTimeTicks: number;
  half: number;
  phase: MatchPhase;
  halfLengthS: number;
  /**
   * The player designated to take the current restart, or -1
   * (`MatchState.restartTaker`, rules/fsm.ts `pickRestartTaker`).
   *
   * Threaded here so `ui/banners.ts` can NAME him — "THROW-IN / OKONKWO" — on
   * the phase feed, which is the only feed that works online (rule events never
   * reach a netcode client). It is on the wire already: `net/snapshot.ts`
   * serialises it in 6 bits, so this costs nothing new.
   */
  restartTaker: number;
  /**
   * The player who WON the last foul — the fouled man, not the offender.
   *
   * ⚠ This is LATCHED, and it has to be. The victim's id exists only on the
   * transient `RuleEvent.victim` for the single tick the foul fires;
   * `MatchState` persists `pendingWhistleFouler` and the favoured TEAM but never
   * the victim's player id, and `StepResult.events` is cleared at the top of the
   * next `step()`. So whoever sees the event first latches it here, and the
   * restart banner that follows a second later can still say who was fouled.
   * -1 until a foul has been seen.
   */
  foulVictim: number;
  /** The player who committed that same foul, or -1. Latched alongside the victim. */
  foulOffender: number;
  /** Team names. Generated/custom only — never a real club (§16). */
  nameA: string;
  nameB: string;
  /** The local player's team (0 = A). */
  localTeam: number;
  localPlayerId: number;
  /**
   * The teammate a currently-charging pass would find — the exact id
   * `choosePassTarget` (pass-target.ts, the SAME scorer the sim strikes with)
   * returns. -1 when no pass is charging or no candidate exists; the radar
   * only highlights a receiver while this is >= 0.
   */
  passTargetId: number;
  /** -1 when the server has never answered a Ping (see connection.ts). */
  rttMs: number;
  /** Frames per second, smoothed. */
  fps: number;
  /** Pitch size for the radar's scale (§9.2 — every mode is a different pitch). */
  pitchLength: number;
  pitchWidth: number;
  /**
   * Which team attacks +X world space RIGHT NOW — flips at half time
   * (rules/fsm.ts). The radar mirrors its whole world->screen mapping around
   * this (see drawRadar's `sign`) so the local player's attacking goal stays
   * on the same screen side across the flip, instead of visibly swapping.
   */
  teamAAttacksPositiveX: boolean;
  /** Live positions of all 22, SoA. Index = PlayerId. */
  px: Float64Array;
  pz: Float64Array;
  active: Uint8Array;
  team: Uint8Array;
  /**
   * Facing/aim yaw, radians — the sim's real per-player yaw (players.yaw,
   * driven by player-controller.ts's turn-rate curve toward the mouse aim),
   * NOT camera direction and NOT ball velocity. Only the controlled player's
   * slot is drawn (the radar direction arrow), but it is threaded SoA like
   * the rest of this block so it flows through the same seam as px/pz/team.
   */
  yaw: Float64Array;
  ballX: number;
  ballZ: number;
  /** Connection banner text; empty = hide. */
  status: string;
  /**
   * Build fingerprint readout (net/fingerprint.ts). Practice sets only the local
   * one; online sets both (local + server) and flags a mismatch so the readout
   * highlights. Empty string = that line is hidden. (§12.6)
   */
  localFingerprint: string;
  serverFingerprint: string;
  fingerprintMismatch: boolean;
  /**
   * Charge power meter (LIVE FOOTBALL POLISH §3): shown while a shot (LMB) or
   * pass (RMB) is being charge-held. `chargeFraction` is the REQUESTED power
   * (0..1, InputManager.chargeFraction against the real SHOT_/PASS_CHARGE_
   * MIN_S/MAX_S) — never the ball's eventual speed. `chargeKind` distinguishes
   * shot vs pass (color/label). The game loop sets `chargeShow = false` the
   * same frame the charge releases, so the meter hides instantly.
   */
  chargeShow: boolean;
  chargeKind: 'shoot' | 'pass';
  chargeFraction: number;

  /**
   * The controlled player's STAMINA (0..1) and live BOOST TIER (0/1/2) for the
   * always-on bar (owner, 2026-08-16: "show the stamina and power bars on the
   * screen somewhere"). The POWER bar already existed as the charge meter, but
   * it only appears while a strike is held; stamina had no readout at all, so
   * the one resource the player has to budget was invisible.
   */
  stamina: number;
  boostTier: number;
  /**
   * Screen-centre aiming crosshair (live ask "show a cross-hair in the middle
   * of the screen … a small point where the ball will go", then "like
   * Valorant or any comp game"). The reticle marks the camera's own forward
   * ray, and `game/crosshairAim.ts` points the strike down that same ray, so
   * it is the aiming mechanism and not a decoration — see that file's header
   * for why the two were NOT already the same number.
   *
   * ALWAYS ON (owner, 2026-07-25: *"keep the cross air always on the
   * screen"*). This was possession-gated — it appeared when you won the ball
   * and vanished when you lost it, which is the one thing a comp-game reticle
   * never does. It is now gated only on the match being LIVE and on the
   * player's own Settings > HUD toggle: the callers still exclude pause,
   * full-time, the intro and the goal replay (a reticle floating over a
   * replay is not "always on", it is a bug), but NOT possession.
   *
   * Gating lives entirely in the callers (main.ts/practice.ts/testArena.ts) —
   * this is a dumb boolean the same way `chargeShow` is, on purpose (§ file
   * header: the HUD only reads).
   */
  crosshairShow: boolean;
  /**
   * Is a strike actually possible right now (the controlled player owns the
   * ball in live play — `game/possessionArrow.ts`'s shared rule)?
   *
   * Splitting this OUT of `crosshairShow` is what lets the reticle be always
   * visible without lying. Armed = full opacity + the ball-destination dot;
   * unarmed = the same four ticks at ~half strength and NO dot, which reads as
   * "you are looking there, but you have no ball to send there". The dot is
   * the specific claim that has to be earned: it is the "point where the ball
   * will go", and with no ball at the feet there is no such point, so drawing
   * it would be the reticle asserting something false rather than merely
   * being present.
   *
   * NOTE this is a DISPLAY state only. It must never be confused with
   * permission to kick — that is the sim's (§3.4 BALL_PLAY_REACH) and the
   * loops' business, and removing the possession gate from the reticle
   * deliberately did not remove it from anything else.
   */
  crosshairArmed: boolean;
  /**
   * Index into crosshair-geometry.ts's CROSSHAIR_SIZE_PRESETS (Settings >
   * HUD > Crosshair size). Read on the same 60Hz timer as the charge meter;
   * drawCrosshair only rewrites the DOM when this actually changes (see the
   * `crosshairSizeApplied` cache below) — a size pick is rare, a steady
   * reticle must never churn layout every frame.
   */
  crosshairSize: number;
}

export function createHudState(): HudState {
  return {
    scoreA: 0, scoreB: 0,
    clockTicks: 0, addedTimeTicks: 0, half: 1,
    phase: MatchPhase.Warmup,
    halfLengthS: 360,
    restartTaker: -1, foulVictim: -1, foulOffender: -1,
    nameA: 'HOME', nameB: 'AWAY',
    localTeam: 0, localPlayerId: 0, passTargetId: -1,
    rttMs: -1, fps: 0,
    pitchLength: 105, pitchWidth: 68, teamAAttacksPositiveX: true,
    px: new Float64Array(MAX_PLAYERS),
    pz: new Float64Array(MAX_PLAYERS),
    active: new Uint8Array(MAX_PLAYERS),
    team: new Uint8Array(MAX_PLAYERS),
    yaw: new Float64Array(MAX_PLAYERS),
    ballX: 0, ballZ: 0,
    status: '',
    localFingerprint: '', serverFingerprint: '', fingerprintMismatch: false,
    chargeShow: false, chargeKind: 'shoot', chargeFraction: 0,
    stamina: 1, boostTier: 0,
    crosshairShow: false, crosshairArmed: false, crosshairSize: DEFAULT_CROSSHAIR_SIZE_INDEX,
  };
}

export interface Hud {
  /** Show/hide the radar — §4.1 binds Tab (hold) to the tactical overlay. */
  setRadarExpanded(expanded: boolean): void;
  dispose(): void;
}

const RADAR_W = 220;
const RADAR_H = 148;
const RADAR_EXPANDED_SCALE = 1.9;
const SCOREBOARD_HZ = 5;
const RADAR_HZ = 20;
/** World-metre length of the controlled-player facing arrow — long enough to
 *  read as a direction at radar scale, short enough not to swallow the dot. */
const ARROW_LEN_M = 6;
/** Power meter fills/hides while a charge is live — a bar that visibly lags a
 *  100ms-hold input reads as broken, so it gets its own fast timer. */
const CHARGE_HZ = 60;

// ---------------------------------------------------------------------------
// Kit colours → CSS. deriveKit returns LINEAR triples (the 3D pipeline wants
// them that way); CSS wants sRGB, so invert the transfer function here. This
// is the single source of truth for team colour — change the kit formula and
// the score bug + radar follow automatically.
// ---------------------------------------------------------------------------

function linearToSrgbHex(rgb: [number, number, number]): string {
  const conv = (c: number): number => {
    const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
  };
  return `#${((1 << 24) | (conv(rgb[0]!) << 16) | (conv(rgb[1]!) << 8) | conv(rgb[2]!)).toString(16).slice(1)}`;
}

const KIT_A = deriveKit(0);
const KIT_B = deriveKit(1);
const TEAM_A_CSS = linearToSrgbHex(KIT_A.primary);
const TEAM_B_CSS = linearToSrgbHex(KIT_B.primary);
const TEAM_A_TRIM_CSS = linearToSrgbHex(KIT_A.secondary);
const TEAM_B_TRIM_CSS = linearToSrgbHex(KIT_B.secondary);

/** Broadcast accent (clock edge, added-time badge, phase strip). */
const ACCENT = '#ffd94a';

function phaseLabel(p: MatchPhase): string {
  switch (p) {
    case MatchPhase.Warmup: return 'WARM UP';
    case MatchPhase.Kickoff: return 'KICK OFF';
    case MatchPhase.InPlay: return '';
    case MatchPhase.BallOut: return 'BALL OUT';
    case MatchPhase.ThrowIn: return 'THROW IN';
    case MatchPhase.Corner: return 'CORNER';
    case MatchPhase.GoalKick: return 'GOAL KICK';
    case MatchPhase.FreeKick: return 'FREE KICK';
    case MatchPhase.Penalty: return 'PENALTY';
    case MatchPhase.DropBall: return 'DROP BALL';
    case MatchPhase.Goal: return 'GOAL!';
    case MatchPhase.Offside: return 'OFFSIDE';
    case MatchPhase.HalfTime: return 'HALF TIME';
    case MatchPhase.FullTime: return 'FULL TIME';
    case MatchPhase.ExtraTime: return 'EXTRA TIME';
    case MatchPhase.PenaltyShootout: return 'SHOOTOUT';
    default: return '';
  }
}

/** mm:ss from sim ticks. The clock counts UP per half, as broadcast does. */
function formatClock(ticks: number, half: number, halfLengthS: number): string {
  // Second-half clocks continue from the first half's end (45:00+), which is
  // what makes a football clock read as a football clock.
  const base = half > 1 ? halfLengthS : 0;
  const total = base + ticks * FIXED_DT;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  // Broadcast bugs zero-pad the minutes ("05:30"), so the clock never jumps
  // width and the skewed segment never reflows.
  return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
}

/**
 * Broadcast 3-letter code from a team name ("Thunder United" → "THU").
 * Fictional names only ever reach here (§16), so no real-club abbreviations
 * can come out of it either.
 */
function teamCode(name: string, fallback: string): string {
  const letters = name.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (letters.length === 0) return fallback;
  return letters.length >= 3 ? letters.slice(0, 3) : (letters + fallback).slice(0, 3);
}

export function mountHud(container: HTMLElement, state: HudState): Hud {
  const root = document.createElement('div');
  root.className = 'fg-hud';
  root.innerHTML = `
    <div class="fg-bug">
      <div class="fg-bug-main">
        <div class="fg-seg fg-seg-clock">
          <span class="fg-unskew fg-clock" data-fg="clock">00:00</span>
        </div>
        <div class="fg-seg fg-seg-added" data-fg="addedWrap">
          <span class="fg-unskew fg-added" data-fg="added"></span>
        </div>
        <div class="fg-seg fg-seg-teams">
          <span class="fg-chip fg-chip-a"></span>
          <span class="fg-unskew fg-code" data-fg="nameA">HOM</span>
          <span class="fg-unskew fg-goals" data-fg="scoreA">0</span>
          <span class="fg-unskew fg-dash">&ndash;</span>
          <span class="fg-unskew fg-goals" data-fg="scoreB">0</span>
          <span class="fg-unskew fg-code" data-fg="nameB">AWY</span>
          <span class="fg-chip fg-chip-b"></span>
        </div>
      </div>
      <div class="fg-bug-phase" data-fg="phaseWrap"><span class="fg-unskew fg-phase" data-fg="phase"></span></div>
    </div>
    <div class="fg-crosshair" data-fg="crosshair">
      <span class="fg-ch-tick" data-fg="chTop"></span>
      <span class="fg-ch-tick" data-fg="chBottom"></span>
      <span class="fg-ch-tick" data-fg="chLeft"></span>
      <span class="fg-ch-tick" data-fg="chRight"></span>
      <span class="fg-ch-dot" data-fg="crosshairDot"></span>
    </div>
    <div class="fg-radar-wrap"><canvas class="fg-radar" width="${RADAR_W}" height="${RADAR_H}"></canvas></div>
    <div class="fg-stam" data-fg="stamWrap">
      <span class="fg-stam-label">STAMINA</span>
      <div class="fg-stam-track"><div class="fg-stam-fill" data-fg="stamFill"></div></div>
    </div>
    <div class="fg-charge" data-fg="chargeWrap">
      <span class="fg-charge-label" data-fg="chargeLabel"></span>
      <div class="fg-charge-track"><div class="fg-charge-fill" data-fg="chargeFill"></div></div>
    </div>
    <div class="fg-net"><span data-fg="fps">-- fps</span><span data-fg="ping"></span></div>
    <div class="fg-fp" data-fg="fpWrap">
      <div class="fg-fp-row"><span class="fg-fp-tag">build</span><span data-fg="fpLocal"></span></div>
      <div class="fg-fp-row" data-fg="fpServerRow"><span class="fg-fp-tag">server</span><span data-fg="fpServer"></span></div>
    </div>
    <div class="fg-status" data-fg="status"></div>
  `;
  container.appendChild(root);

  const style = document.createElement('style');
  style.textContent = HUD_CSS;
  document.head.appendChild(style);

  const q = (name: string): HTMLElement => root.querySelector(`[data-fg="${name}"]`) as HTMLElement;
  const el = {
    nameA: q('nameA'), nameB: q('nameB'),
    scoreA: q('scoreA'), scoreB: q('scoreB'),
    clock: q('clock'), added: q('added'), addedWrap: q('addedWrap'),
    phase: q('phase'), phaseWrap: q('phaseWrap'),
    fps: q('fps'), ping: q('ping'), status: q('status'),
    fpWrap: q('fpWrap'), fpLocal: q('fpLocal'),
    fpServer: q('fpServer'), fpServerRow: q('fpServerRow'),
    chargeWrap: q('chargeWrap'), chargeLabel: q('chargeLabel'), chargeFill: q('chargeFill'),
    stamWrap: q('stamWrap'), stamFill: q('stamFill'),
    crosshair: q('crosshair'), crosshairDot: q('crosshairDot'),
    chTop: q('chTop'), chBottom: q('chBottom'), chLeft: q('chLeft'), chRight: q('chRight'),
  };
  const canvas = root.querySelector('.fg-radar') as HTMLCanvasElement;
  const wrap = root.querySelector('.fg-radar-wrap') as HTMLElement;
  const ctx = canvas.getContext('2d');

  // Cache the last written string per node. `textContent =` on an unchanged
  // value still dirties layout; at 5Hz that is not a perf issue, but the whole
  // point of this file is that the HUD never becomes one. Same guard for
  // show/hide — `style.display =` is only touched on transitions.
  const last: Record<string, string> = {};
  const setText = (node: HTMLElement, key: string, value: string): void => {
    if (last[key] === value) return;
    last[key] = value;
    node.textContent = value;
  };
  const setShown = (node: HTMLElement, key: string, shown: boolean, display: string): void => {
    const v = shown ? display : 'none';
    if (last[key] === v) return;
    last[key] = v;
    node.style.display = v;
  };

  const drawScoreboard = (): void => {
    setText(el.nameA, 'nameA', teamCode(state.nameA, 'HOM'));
    setText(el.nameB, 'nameB', teamCode(state.nameB, 'AWY'));
    setText(el.scoreA, 'scoreA', String(state.scoreA));
    setText(el.scoreB, 'scoreB', String(state.scoreB));
    setText(el.clock, 'clock', formatClock(state.clockTicks, state.half, state.halfLengthS));
    const added = state.addedTimeTicks > 0
      ? `+${Math.ceil((state.addedTimeTicks * FIXED_DT) / 60)}`
      : '';
    setText(el.added, 'added', added);
    setShown(el.addedWrap, 'addedWrap', added !== '', 'flex');
    const phase = phaseLabel(state.phase);
    setText(el.phase, 'phase', phase);
    setShown(el.phaseWrap, 'phaseWrap', phase !== '', 'inline-flex');
    setText(el.fps, 'fps', `${Math.round(state.fps)} fps`);
    setText(el.ping, 'ping', state.rttMs >= 0 ? `${Math.round(state.rttMs)} ms` : '');
    setText(el.status, 'status', state.status);
    setShown(el.status, 'statusShown', state.status !== '', 'block');

    // Build fingerprint (§12.6). Practice: local only. Online: local + server,
    // the whole block turning red when they disagree so the mismatch is unmissable.
    setText(el.fpLocal, 'fpLocal', state.localFingerprint);
    setText(el.fpServer, 'fpServer', state.serverFingerprint);
    setShown(el.fpWrap, 'fpWrap', state.localFingerprint !== '', 'block');
    setShown(el.fpServerRow, 'fpServerRow', state.serverFingerprint !== '', 'flex');
    const mismatchClass = state.fingerprintMismatch ? 'fg-fp fg-fp-bad' : 'fg-fp';
    if (last.fpClass !== mismatchClass) {
      last.fpClass = mismatchClass;
      el.fpWrap.className = mismatchClass;
    }
  };

  const drawRadar = (): void => {
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const pad = 6;
    const iw = w - pad * 2;
    const ih = h - pad * 2;

    ctx.fillStyle = 'rgba(8,26,12,0.72)';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.strokeRect(pad, pad, iw, ih);

    // ── Orientation (mirroring across half-time) ────────────────────────────
    // world.teamAAttacksPositiveX flips at half time (rules/fsm.ts), so a
    // literal world->screen mapping would visibly swap which side the local
    // player's goal sits on the instant the half turns over. `computeMirrorSign`
    // (minimap-geometry.ts, unit-tested there) instead decides whether the
    // WHOLE map rotates 180° (negate BOTH x and z — never just one axis, which
    // would mirror-flip handedness and put a winger's real flank on the wrong
    // side) so the local player always attacks screen-right / defends
    // screen-left, in either half, on either team. `sign` feeds sx()/sz()
    // below and nothing else — every shape (goals, boxes, dots, the facing
    // arrow) is computed in WORLD space and pushed through the same two
    // functions, so it rotates together and stays geometrically consistent;
    // there is no separate "screen-space" branch anywhere to get the flip
    // wrong in.
    const sign = computeMirrorSign(state.localTeam, state.teamAAttacksPositiveX);

    // World -> radar. The pitch's long axis (X) maps to the radar's width.
    const sx = (x: number): number => projectX(x, sign, state.pitchLength, pad, iw);
    const sz = (z: number): number => projectZ(z, sign, state.pitchWidth, pad, ih);
    const xScale = iw / state.pitchLength;
    const zScale = ih / state.pitchWidth;
    const halfLength = state.pitchLength / 2;

    // Halfway line + centre circle: the minimum needed to read which third the
    // play is in at a glance. Both are symmetric about the pitch centre, so
    // the 180° rotation above is a no-op for them (sx(0)/sz(0) never move).
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sx(0), pad);
    ctx.lineTo(sx(0), pad + ih);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(sx(0), sz(0), CENTRE_CIRCLE_RADIUS * xScale, CENTRE_CIRCLE_RADIUS * zScale, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Goals: mouth line + a shallow depth outline at both ends. GOAL_WIDTH
    // (7.32m) is far smaller than even the narrowest squad-size pitch (25m for
    // 1v1), so unlike the penalty box below this never needs a fits check.
    for (const dir of [-1, 1] as const) {
      const goalX = dir * halfLength;
      const mouthX = sx(goalX);
      const backX = sx(goalX + dir * GOAL_DEPTH);
      const z0 = sz(-GOAL_WIDTH / 2);
      const z1 = sz(GOAL_WIDTH / 2);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(mouthX, z0);
      ctx.lineTo(mouthX, z1);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        Math.min(mouthX, backX), Math.min(z0, z1),
        Math.abs(backX - mouthX), Math.abs(z1 - z0),
      );
    }

    // Penalty boxes. PENALTY_AREA_WIDTH (40.32m) is a fixed Law-of-the-Game
    // size regardless of squad-size pitch preset — the 1v1/2v2/3v3 pitches are
    // at or narrower than that (SQUAD_PRESETS width 25/30/40), so drawing it
    // there would just be a rectangle already touching both touchlines: noise,
    // not signal. Only draw when the box geometrically fits inside the pitch.
    const boxFits = PENALTY_AREA_WIDTH < state.pitchWidth && PENALTY_AREA_LENGTH * 2 < state.pitchLength;
    if (boxFits) {
      ctx.strokeStyle = 'rgba(255,255,255,0.28)';
      ctx.lineWidth = 1;
      for (const dir of [-1, 1] as const) {
        const goalX = dir * halfLength;
        const nearX = sx(goalX);
        const farX = sx(goalX - dir * PENALTY_AREA_LENGTH);
        const z0 = sz(-PENALTY_AREA_WIDTH / 2);
        const z1 = sz(PENALTY_AREA_WIDTH / 2);
        ctx.strokeRect(
          Math.min(nearX, farX), Math.min(z0, z1),
          Math.abs(farX - nearX), Math.abs(z1 - z0),
        );
      }
    }

    // §4.7 "your assigned zone": the defensive third you are responsible for.
    // With the rotation above the local player always attacks screen-right, so
    // this is always the screen-LEFT third — no team/half branch needed. (The
    // old `localTeam === 0 ? left : right` branch this replaces did not account
    // for the half-time flip, so it silently pointed at the wrong third for
    // team B, or for either team after half time.)
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    const thirdW = iw / 3;
    ctx.fillRect(pad, pad, thirdW, ih);

    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (state.active[i] !== 1) continue;
      const isLocal = i === state.localPlayerId;
      const isPassTarget = state.passTargetId >= 0 && i === state.passTargetId;
      const x = sx(state.px[i]!);
      const z = sz(state.pz[i]!);

      if (isPassTarget) {
        // Selected pass receiver while a pass is charging — the SAME target
        // choosePassTarget (pass-target.ts) hands the sim to actually strike.
        ctx.beginPath();
        ctx.arc(x, z, 5.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,217,74,0.4)';
        ctx.fill();
      }

      if (isLocal) {
        // Soft glow halo UNDER the dot so the controlled player reads as
        // "bigger and brighter", not just ringed, among 21 same-size dots.
        ctx.beginPath();
        ctx.arc(x, z, 7, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,217,74,0.28)';
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(x, z, isLocal ? 4.6 : 2.6, 0, Math.PI * 2);
      ctx.fillStyle = state.team[i] === 0 ? TEAM_A_CSS : TEAM_B_CSS;
      ctx.fill();

      if (isLocal) {
        // The controlled player must be findable instantly — same job as the
        // ring in the 3D scene (§4.8).
        ctx.strokeStyle = ACCENT;
        ctx.lineWidth = 1.7;
        ctx.stroke();

        // Facing/aim direction arrow — the sim's REAL yaw (players.yaw, set by
        // player-controller.ts's turn-rate curve toward the mouse aim), never
        // camera yaw and never ball velocity. projectArrowTip computes the tip
        // in WORLD space and pushes it through the SAME projectX/projectZ as
        // the dot itself, so it inherits the 180° rotation automatically — no
        // separate "yaw + PI when mirrored" branch to get backwards.
        const yaw = state.yaw[i]!;
        const tip = projectArrowTip(
          state.px[i]!, state.pz[i]!, yaw, ARROW_LEN_M,
          sign, state.pitchLength, state.pitchWidth, pad, iw, ih,
        );
        const tipX = tip.x;
        const tipZ = tip.z;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(x, z);
        ctx.lineTo(tipX, tipZ);
        ctx.stroke();
        const ang = Math.atan2(tipZ - z, tipX - x);
        const headLen = 3.2;
        ctx.beginPath();
        ctx.moveTo(tipX, tipZ);
        ctx.lineTo(tipX - headLen * Math.cos(ang - Math.PI / 6), tipZ - headLen * Math.sin(ang - Math.PI / 6));
        ctx.lineTo(tipX - headLen * Math.cos(ang + Math.PI / 6), tipZ - headLen * Math.sin(ang + Math.PI / 6));
        ctx.closePath();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
      }
    }

    // Ball marker — a dark outline so it never blends into a white-kit dot.
    ctx.beginPath();
    ctx.arc(sx(state.ballX), sz(state.ballZ), 2.8, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  /** Always-on stamina bar; its colour follows the boost tier the sim reports. */
  const drawStamina = (): void => {
    const f = state.stamina < 0 ? 0 : state.stamina > 1 ? 1 : state.stamina;
    el.stamFill.style.width = `${(f * 100).toFixed(1)}%`;
    const cls = state.boostTier >= 2 ? 'fg-stam fg-stam-b2'
      : state.boostTier === 1 ? 'fg-stam fg-stam-b1'
      : f <= 0.2 ? 'fg-stam fg-stam-low' : 'fg-stam';
    if (last['stamClass'] !== cls) {
      last['stamClass'] = cls;
      el.stamWrap.className = cls;
    }
  };

  const drawCharge = (): void => {
    setShown(el.chargeWrap, 'chargeWrap', state.chargeShow, 'flex');
    if (!state.chargeShow) return;
    const isShoot = state.chargeKind === 'shoot';
    setText(el.chargeLabel, 'chargeLabel', isShoot ? 'SHOT' : 'PASS');
    const cls = isShoot ? 'fg-charge fg-charge-shoot' : 'fg-charge fg-charge-pass';
    if (last.chargeClass !== cls) {
      last.chargeClass = cls;
      el.chargeWrap.className = cls;
    }
    const frac = state.chargeFraction < 0 ? 0 : state.chargeFraction > 1 ? 1 : state.chargeFraction;
    el.chargeFill.style.width = `${(frac * 100).toFixed(1)}%`;
  };

  let crosshairOn = false;
  /** Last written armed state. `null` = never written, so the first live frame
   *  always stamps the class (the same reason crosshairSizeApplied is -1). */
  let crosshairArmedApplied: boolean | null = null;
  // Sentinel (no real preset index) so the FIRST live frame always applies the
  // tick/dot geometry once, rather than requiring size to visibly "change".
  let crosshairSizeApplied = -1;
  const applyBox = (node: HTMLElement, box: CrosshairBox): void => {
    node.style.left = `${box.left}px`;
    node.style.top = `${box.top}px`;
    node.style.width = `${box.width}px`;
    node.style.height = `${box.height}px`;
  };
  const drawCrosshair = (): void => {
    if (state.crosshairShow !== crosshairOn) {
      crosshairOn = state.crosshairShow;
      el.crosshair.classList.toggle('on', crosshairOn);
    }
    if (!crosshairOn) return;
    // ARMED vs IDLE (see HudState.crosshairArmed): the ticks dim and the
    // ball-destination dot goes away entirely when there is no ball to kick, so
    // the reticle stays present without claiming a destination it cannot
    // deliver. Toggled on CHANGE only, same discipline as setText/setShown —
    // this runs at CHARGE_HZ and must not churn the class list every frame.
    if (state.crosshairArmed !== crosshairArmedApplied) {
      crosshairArmedApplied = state.crosshairArmed;
      el.crosshair.classList.toggle('armed', state.crosshairArmed);
    }
    // Size only changes when the player edits Settings > HUD (rare) — cache it
    // so a steady reticle never rewrites four elements' layout every frame,
    // same "never churn identical frames" discipline as setText/setShown.
    if (state.crosshairSize !== crosshairSizeApplied) {
      crosshairSizeApplied = state.crosshairSize;
      const preset = CROSSHAIR_SIZE_PRESETS[clampCrosshairSizeIndex(crosshairSizeApplied)]!;
      const ticks = crosshairTicks(preset);
      applyBox(el.chTop, ticks.top);
      applyBox(el.chBottom, ticks.bottom);
      applyBox(el.chLeft, ticks.left);
      applyBox(el.chRight, ticks.right);
      applyBox(el.crosshairDot, crosshairDotBox(preset));
    }
    // The centre dot (the "point where the ball will go") swells + brightens as
    // a strike charges, so the reticle reads as locking on. Unchanged from the
    // original: the base box above comes from the size preset, this scales it
    // further via a CSS transform (default transform-origin is the box's own
    // centre, which crosshairDotBox already centres on the reticle's origin —
    // so it grows evenly outward, never drifting off dead-centre).
    const frac = state.chargeShow
      ? state.chargeFraction < 0 ? 0 : state.chargeFraction > 1 ? 1 : state.chargeFraction
      : 0;
    el.crosshairDot.style.transform = `scale(${(1 + frac * 1.4).toFixed(2)})`;
  };

  drawScoreboard();
  drawRadar();
  drawStamina();
  drawCharge();
  const scoreTimer = window.setInterval(drawScoreboard, 1000 / SCOREBOARD_HZ);
  const radarTimer = window.setInterval(drawRadar, 1000 / RADAR_HZ);
  const chargeTimer = window.setInterval(() => { drawStamina(); drawCharge(); drawCrosshair(); }, 1000 / CHARGE_HZ);

  return {
    setRadarExpanded(expanded: boolean): void {
      wrap.style.transform = expanded ? `scale(${RADAR_EXPANDED_SCALE})` : 'scale(1)';
    },
    dispose(): void {
      window.clearInterval(scoreTimer);
      window.clearInterval(radarTimer);
      window.clearInterval(chargeTimer);
      root.remove();
      style.remove();
    },
  };
}

/**
 * EA-FC broadcast layout (D0): score bug pinned top-left, radar bottom-left,
 * net stats top-right (the dev overlay panel sits below them at ~y320, so they
 * never collide). Everything fits comfortably at 1280×720.
 *
 * The angular look is one trick used consistently: segments skew -10°, their
 * text counter-skews +10° so glyphs stay upright, and the kit chips are left
 * skewed on purpose — parallelogram chips ARE the broadcast look.
 *
 * `pointer-events: none` throughout — the canvas below owns the pointer lock,
 * and a HUD element eating a click would break aiming.
 */
const HUD_CSS = `
.fg-hud {
  position: fixed; inset: 0; pointer-events: none;
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, -apple-system, sans-serif;
  color: #fff; user-select: none;
}
.fg-crosshair {
  /* Zero-size anchor pinned to the exact viewport centre by a CSS PERCENTAGE
     (never a computed pixel offset), so every child positioned with a plain
     left/top in px (see drawCrosshair/applyBox) sits at a fixed offset from
     DEAD centre regardless of viewport size or aspect — see
     crosshair-geometry.ts's header + crosshairCentre for the pinned contract. */
  position: absolute; left: 50%; top: 50%;
  width: 0; height: 0; transform: translate(-50%, -50%);
  opacity: 0; transition: opacity 0.12s ease;
}
/* ALWAYS ON, but not always claiming a shot. The .on class = drawn at all (live
   match, not paused/replaying, not switched off in Settings). The .armed class =
   the controlled player actually owns the ball, so a strike would fire along
   this ray. Idle sits at ~half strength: unmistakably present (the owner asked for
   it to stop disappearing) yet visibly not the same state as having the ball,
   which is what stops an always-on reticle from reading as "you can shoot" the
   whole match. Both go through the existing 0.12s opacity transition, so
   winning or losing the ball EASES between the two instead of blinking. */
.fg-crosshair.on { opacity: 0.45; }
.fg-crosshair.on.armed { opacity: 0.9; }
/* Four independent ticks (geometry from crosshair-geometry.ts, applied as
   inline left/top/width/height by drawCrosshair) — the defining Valorant/CS
   shape: short hairlines pulled away from a centre gap, not a continuous "+".
   Hard 1px outline via box-shadow at 0 BLUR / 1px SPREAD — NOT the old
   blurred glow (0 0 2px) — so edges stay crisp at every distance and the
   line reads on both bright grass and dark stands (white fill for contrast
   against the stands, black hairline outline for contrast against the grass). */
.fg-ch-tick {
  position: absolute; background: #ffffff;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.9);
}
.fg-ch-dot {
  /* Same crisp hard-outline treatment as the ticks, in a DIFFERENT colour
     (bright green vs the ticks' white) so the ball-destination dot always
     reads as visually distinct from the reticle around it.
     DISPLAY:NONE BY DEFAULT — the dot is the reticle's one factual claim
     ("the ball goes HERE") and it is only drawn while armed, i.e. while
     there is a ball at the feet to send. Not merely faded: a faded dot is
     still a dot pointing at a destination that does not exist. */
  position: absolute; border-radius: 50%; display: none;
  background: #39ff9a; box-shadow: 0 0 0 1px rgba(0,0,0,0.9);
  transition: transform 0.06s linear;
}
.fg-crosshair.armed .fg-ch-dot { display: block; }
.fg-bug {
  position: absolute; top: 14px; left: 18px;
  filter: drop-shadow(0 3px 8px rgba(0,0,0,0.45));
}
.fg-bug-main { display: flex; align-items: stretch; height: 34px; gap: 2px; }
.fg-seg { display: flex; align-items: center; transform: skewX(-10deg); }
.fg-unskew { transform: skewX(10deg); display: inline-block; }
.fg-seg-clock {
  background: linear-gradient(180deg, #3a4254 0%, #1c212e 55%, #131722 100%);
  border-left: 3px solid ${ACCENT};
  border-radius: 3px 0 0 3px;
  padding: 0 12px 0 14px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.10);
}
.fg-clock {
  font-variant-numeric: tabular-nums; font-weight: 800; font-size: 15px;
  letter-spacing: 0.5px; text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}
.fg-seg-added {
  display: none; /* shown by JS when added time exists */
  background: linear-gradient(180deg, #ffe27a 0%, #f5c334 100%);
  color: #171b26; padding: 0 9px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
}
.fg-added { font-weight: 900; font-size: 13px; font-variant-numeric: tabular-nums; }
.fg-seg-teams {
  background: linear-gradient(180deg, rgba(32,37,50,0.97) 0%, rgba(14,17,25,0.95) 100%);
  border-radius: 0 3px 3px 0;
  padding: 0 13px; gap: 9px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
}
.fg-chip { width: 7px; height: 19px; border-radius: 1px; }
.fg-chip-a { background: ${TEAM_A_CSS}; box-shadow: inset 0 2px 0 ${TEAM_A_TRIM_CSS}, 0 0 4px rgba(0,0,0,0.35); }
.fg-chip-b { background: ${TEAM_B_CSS}; box-shadow: inset 0 2px 0 ${TEAM_B_TRIM_CSS}, 0 0 4px rgba(0,0,0,0.35); }
.fg-code { font-size: 14px; font-weight: 800; letter-spacing: 1.4px; text-shadow: 0 1px 2px rgba(0,0,0,0.5); }
.fg-goals {
  font-size: 18px; font-weight: 900; min-width: 16px; text-align: center;
  font-variant-numeric: tabular-nums; text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}
.fg-dash { opacity: 0.4; font-weight: 700; margin: 0 -3px; }
.fg-bug-phase {
  display: none; /* shown by JS when a phase label exists */
  margin-top: 4px; margin-left: 8px;
  transform: skewX(-10deg);
  background: linear-gradient(180deg, #ffe27a 0%, #f5c334 100%);
  color: #171b26; border-radius: 2px;
  padding: 2px 12px;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
}
.fg-phase { font-size: 11px; font-weight: 900; letter-spacing: 1.8px; }
.fg-radar-wrap {
  position: absolute; left: 16px; bottom: 16px;
  transform-origin: left bottom; transition: transform 120ms ease-out;
}
.fg-radar {
  display: block; border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.16);
  border-top: 2px solid rgba(255,217,74,0.55);
  box-shadow: 0 3px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06);
}
.fg-net {
  position: absolute; top: 16px; right: 16px;
  display: flex; gap: 10px; font-size: 11px; font-variant-numeric: tabular-nums;
  background: rgba(10,14,24,0.6); padding: 4px 8px; border-radius: 3px; opacity: 0.85;
}
.fg-stam {
  /* BOTTOM RIGHT, not bottom centre: centre is where the commentary banner
     lives and the bar disappeared behind it (measured in a capture). Right of
     the pitch keeps it in the eye's periphery next to the action without
     fighting the radar on the left. */
  position: absolute; right: 18px; bottom: 22px;
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
}
.fg-stam-label {
  font-size: 9px; font-weight: 900; letter-spacing: 2px;
  color: rgba(255,255,255,0.72); text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.fg-stam-track {
  width: 180px; height: 7px; border-radius: 4px;
  background: rgba(10,14,24,0.7);
  border: 1px solid rgba(255,255,255,0.22);
  overflow: hidden;
}
.fg-stam-fill {
  height: 100%; width: 100%;
  background: linear-gradient(90deg, #7ef0a8, #23c268);
  /* No transition: stamina is being spent RIGHT NOW, and a lagging bar teaches
     the wrong thing about when it is safe to sprint. */
}
.fg-stam-low .fg-stam-fill { background: linear-gradient(90deg, #ffb457, #ff6a3d); }
.fg-stam-b1 .fg-stam-fill { background: linear-gradient(90deg, #ffd76a, #ff9f1c); }
.fg-stam-b2 .fg-stam-fill { background: linear-gradient(90deg, #ff8a5a, #ff2f2f); }
.fg-charge {
  display: none; /* shown by JS while a shot/pass charge is held */
  position: absolute; left: 50%; bottom: 54px; transform: translateX(-50%);
  flex-direction: column; align-items: center; gap: 3px;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
}
.fg-charge-label {
  font-size: 11px; font-weight: 900; letter-spacing: 2px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
}
.fg-charge-track {
  width: 180px; height: 10px; border-radius: 5px;
  background: rgba(10,14,24,0.7);
  border: 1px solid rgba(255,255,255,0.25);
  overflow: hidden;
}
.fg-charge-fill {
  height: 100%; width: 0%;
  border-radius: 5px 0 0 5px;
  /* width is written directly per-frame (drawCharge) — no CSS transition, or
     a fast release would visibly lag the actual hold duration. */
}
.fg-charge-shoot .fg-charge-label { color: #ff6a4a; }
.fg-charge-shoot .fg-charge-fill { background: linear-gradient(90deg, #ff8a5a, #ff4a2f); }
.fg-charge-pass .fg-charge-label { color: #5ad1ff; }
.fg-charge-pass .fg-charge-fill { background: linear-gradient(90deg, #7fe0ff, #2fa8e0); }
.fg-fp {
  position: absolute; top: 40px; right: 16px;
  display: none; /* shown by JS once a local fingerprint exists */
  flex-direction: column; gap: 1px;
  font: 10px ui-monospace, 'SFMono-Regular', Menlo, monospace;
  color: rgba(255,255,255,0.62);
  background: rgba(10,14,24,0.55); padding: 3px 8px; border-radius: 3px;
  text-align: right; letter-spacing: 0.2px;
}
.fg-fp-row { display: flex; gap: 8px; justify-content: flex-end; }
.fg-fp-tag { opacity: 0.45; text-transform: uppercase; letter-spacing: 0.6px; }
.fg-fp-bad {
  color: #ffd0d0;
  background: rgba(120,20,24,0.72);
  box-shadow: 0 0 0 1px rgba(255,90,90,0.6);
}
.fg-fp-bad .fg-fp-tag { opacity: 0.8; }
.fg-status {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
  background: linear-gradient(180deg, rgba(24,28,40,0.95), rgba(12,15,22,0.93));
  border-left: 3px solid ${ACCENT};
  padding: 10px 18px; border-radius: 4px;
  font-size: 14px; font-weight: 600; letter-spacing: 0.3px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
  display: none;
}
`;
